import React, { useEffect, useRef, useState } from 'react';
import notifyHB from '../../config/notifyHB';
import { toast } from '@/components/toast/toast';
import cognitoConfig from '../../config/cognito';
import { sendOnboardingOtp } from '../../use-cases/send-onboarding-otp';
import styles from './otp.module.css';
import { Button } from '@/components/ui/button';
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from '@/components/ui/input-otp';
import { Loader2Icon } from 'lucide-react';
import { OTP_TTL_SECONDS } from '../login/otpConfirmation';
import { confirmPhoneOtp } from '@/use-cases/confirm-phone-otp';
import { useAuthContext } from '@/components/auth-context/auth-context';
import { initiateKYC } from '@/use-cases/initiate-kyc';
import { getHttpErrorCode } from '@/utils/get-http-error-code';
import { getErrorReason, trackEvent } from '@/utils/analytics';

interface RegisterProps {
	nextStep: () => void;
	prevStep: () => void;
	// True when the wizard resumes here, so no summary step has sent a code.
	sendOtpOnMount?: boolean;
}

const OTP_LENGTH = 6;

const OtpComponent: React.FC<RegisterProps> = ({
	nextStep,
	prevStep,
	sendOtpOnMount = false,
}) => {
	const { setKycUrl } = useAuthContext();
	const [isLoading, setIsLoading] = useState(false);
	const [userInput, setUserInput] = useState('');
	const [validSeconds, setValidSeconds] = useState(OTP_TTL_SECONDS);
	const sentOnMountRef = useRef(false);

	useEffect(() => {
		const intervalId = setInterval(() => {
			setValidSeconds((currentValidSeconds) =>
				currentValidSeconds > 0
					? currentValidSeconds - 1
					: currentValidSeconds,
			);
		}, 1_000);

		return () => clearInterval(intervalId);
	}, []);

	const handleSendOtp = async () => {
		const onboardingUlid: string =
			localStorage.getItem('onboarding-ulid') || '';
		setIsLoading(true);
		try {
			await sendOnboardingOtp(onboardingUlid);
			setUserInput('');
			setValidSeconds(OTP_TTL_SECONDS);
		} catch (exception) {
			console.error(exception);
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (!sendOtpOnMount || sentOnMountRef.current) return;
		sentOnMountRef.current = true;
		handleSendOtp();
	}, []);

	const handleVerifyOtp: React.FormEventHandler<HTMLFormElement> = async (
		event,
	) => {
		event.preventDefault();
		if (!userInput) {
			toast({ mode: 'caution', description: 'Ingrese un otp valido' });
			return;
		}

		let failedCall = 'phone_otp_confirm';
		try {
			setIsLoading(true);
			const onboardingUlid: string =
				localStorage.getItem('onboarding-ulid') || '';

			const verifyResponse = await confirmPhoneOtp(
				onboardingUlid,
				userInput,
				cognitoConfig.cognitoClient,
			).catch((error: unknown) => {
				// INVALID_OTP is the only wrong-code signal; this endpoint also
				// returns 400 for missing params and unexpected server errors.
				if (getHttpErrorCode(error) === 'INVALID_OTP') {
					return null;
				}
				throw error;
			});

			if (verifyResponse === null) {
				toast({ mode: 'caution', description: 'Código inválido' });
				trackEvent('onboarding_error', {
					step: 'phone_otp',
					reason: 'invalid_code',
				});
			} else if (verifyResponse.status === 200) {
				failedCall = 'kyc_init';
				const initiateKycResponse = await initiateKYC(
					onboardingUlid,
					cognitoConfig.cognitoClient,
				);
				if (initiateKycResponse.status === 200) {
					trackEvent('onboarding_sms_confirmed');
					setKycUrl(initiateKycResponse.kyc_url);
					nextStep();
				} else {
					trackEvent('onboarding_error', {
						step: 'phone_otp',
						reason: 'kyc_init_unexpected_status',
					});
				}
			} else {
				trackEvent('onboarding_error', {
					step: 'phone_otp',
					reason: 'phone_otp_unexpected_status',
				});
			}

			setIsLoading(false);
		} catch (error) {
			trackEvent('onboarding_error', {
				step: 'phone_otp',
				reason: `${failedCall}_${getErrorReason(error)}`,
			});
			notifyHB('request-signature', 'OtpComponent', error);
			setIsLoading(false);
		}
	};

	return (
		<form onSubmit={handleVerifyOtp} className={styles.otp}>
			<span className={styles.title}>
				Ingresa el código que te enviamos por SMS
			</span>
			<div>
				<InputOTP
					autoFocus
					value={userInput}
					onChange={setUserInput}
					maxLength={OTP_LENGTH}
					inputMode="text"
				>
					<InputOTPGroup>
						<InputOTPSlot index={0} />
						<InputOTPSlot index={1} />
						<InputOTPSlot index={2} />
					</InputOTPGroup>
					<InputOTPSeparator />
					<InputOTPGroup>
						<InputOTPSlot index={3} />
						<InputOTPSlot index={4} />
						<InputOTPSlot index={5} />
					</InputOTPGroup>
				</InputOTP>
			</div>

			<div className={styles.input}>
				El OTP caducará en {Math.floor(validSeconds / 60)}:
				{(validSeconds % 60).toString().padStart(2, '0')}
			</div>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 12,
				}}
			>
				<Button
					variant="default"
					type="submit"
					disabled={isLoading || userInput.length != OTP_LENGTH}
				>
					{isLoading && <Loader2Icon className="animate-spin" />}
					Continuar
				</Button>
				<Button
					variant="ghost"
					onClick={handleSendOtp}
					disabled={isLoading}
				>
					Reenviar OTP
				</Button>
				<Button
					variant="secondary"
					onClick={prevStep}
					disabled={isLoading}
				>
					Regresar
				</Button>
			</div>
		</form>
	);
};

export default OtpComponent;
